/** Organization forms and UI state; HTTP validators are generated from OpenAPI. */

import { z } from "zod";
import { organizationSchema, organizationsListResponseSchema } from "@/generated/organization-contract";
import { orgGrantSchema, orgGrantListSchema } from "./grants.generated";
export { organizationSchema, organizationsListResponseSchema };

/** Population archetypes — the org's sign-in door vocabulary. The
 *  display copy lives with the tenant-level editor
 *  (identity-trust-settings/schemas.ts, ARCHETYPE_LABELS) and is
 *  reused, not duplicated. */
export const orgArchetypeSchema = z.enum([
  "workforce_iam",
  "standalone_iam",
  "consumer_ciam",
]);
export type OrgArchetype = z.infer<typeof orgArchetypeSchema>;

export const orgStatusSchema = z.enum(["active", "suspended", "deactivated"]);
export type OrgStatus = z.infer<typeof orgStatusSchema>;

export type Organization = z.infer<typeof organizationSchema>;
export type OrganizationsListResponse = z.infer<typeof organizationsListResponseSchema>;

/** URL-backed list state (validated by the route's search schema). */
export const organizationsListSearchSchema = z.object({
  status: orgStatusSchema.optional().catch(undefined),
  cursor: z.string().optional().catch(undefined),
});
export type OrganizationsListSearch = z.infer<
  typeof organizationsListSearchSchema
>;

export interface CreateOrganizationInput {
  name: string;
  slug: string;
  display_name?: string;
  contact_name?: string;
  contact_email?: string;
  /** Customer tenants only — the backend refuses it elsewhere. */
  max_users?: number;
}

export interface UpdateOrganizationInput {
  name?: string;
  display_name?: string;
  contact_name?: string;
  contact_email?: string;
  /** null clears the cap; undefined leaves it untouched. */
  max_users?: number | null;
}

/** The organization page's tabs; the URL carries the choice. */
export const organizationTabSchema = z.enum(["overview", "members", "requests", "sign-in", "provisioning", "delegated", "settings"]);
export type OrganizationTab = z.infer<typeof organizationTabSchema>;

// ── Delegated access (F79) ──────────────────────────────────────────────────
//
// An organization grant gives one of the tenant's admins an organization-scope
// role over this organization: the confined organization console. The wire
// shape is generated from the operations contract.

export { orgGrantSchema, orgGrantListSchema };

export const orgGrantRoleSchema = orgGrantSchema.shape.role;
export type OrgGrantRole = z.infer<typeof orgGrantRoleSchema>;

export const ORG_GRANT_ROLE_LABEL: Record<OrgGrantRole, string> = {
  org_admin: "Organization admin",
  org_operator: "Organization operator",
};

export type OrgGrant = z.infer<typeof orgGrantSchema>;

export interface CreateOrgGrantInput {
  admin_user_id: string;
  role: OrgGrantRole;
  /** RFC 3339; the server defaults and caps the window. */
  expires_at?: string;
}
